import { Actor } from "../actor.js";
import { Layer } from "../tile.js";
import { actorDB, getTagFlag } from "../const.js";
import { hasOwnProperty } from "../helpers.js";
import { WireOverlapMode } from "../wires.js";
import { onLevelAfterTick } from "../level.js";
export class Wall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "wall";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks() {
        return true;
    }
}
Wall.tags = ["wall", "tinnable"];
actorDB["wall"] = Wall;
export class SteelWall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "steelWall";
        this.wireOverlapMode = WireOverlapMode.CROSS;
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks() {
        return true;
    }
}
SteelWall.tags = ["blocks-ghost"];
SteelWall.immuneTags = ["tnt"];
actorDB["steelWall"] = SteelWall;
export class CustomWall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "customWall";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks() {
        return true;
    }
}
CustomWall.tags = ["blocks-ghost"];
actorDB["customWall"] = CustomWall;
function doorFactory(color) {
    var _a;
    const sentenceCaseName = color[0].toUpperCase() + color.substr(1).toLowerCase();
    return _a = class extends Actor {
            constructor() {
                super(...arguments);
                this.id = `door${sentenceCaseName}`;
            }
            getLayer() {
                return Layer.STATIONARY;
            }
            blocks(other) {
                var _a;
                return !(((_a = other.inventory.keys[`key${sentenceCaseName}`]) === null || _a === void 0 ? void 0 : _a.amount) > 0);
            }
            actorCompletelyJoined(other) {
                var _a, _b, _c, _d;
                if (!((_a = other.inventory.keys[`key${sentenceCaseName}`]) === null || _a === void 0 ? void 0 : _a.amount))
                    return;
                if (other.hasTag("playable")) {
                    (_b = this.level.sfxManager) === null || _b === void 0 ? void 0 : _b.playOnce("door unlock");
                }
                other.inventory.keys[`key${sentenceCaseName}`].amount--;
                (_d = (_c = other.inventory.keys[`key${sentenceCaseName}`].type).keyUsed) === null || _d === void 0 ? void 0 : _d.call(_c, other);
                this.destroy(null, null);
            }
        },
        _a.tags = ["door"],
        _a.blockTags = ["normal-monster", "cc1block"],
        _a;
}
actorDB["doorBlue"] = doorFactory("blue");
actorDB["doorRed"] = doorFactory("red");
actorDB["doorGreen"] = doorFactory("green");
actorDB["doorYellow"] = doorFactory("yellow");
const shortDirNames = "URDL";
export class ThinWall extends Actor {
    constructor(level, pos, customData, wires) {
        super(level, pos, customData, wires);
        this.id = "thinWall";
        this.allowedDirections = Array.from(this.customData)
            .map(val => shortDirNames.includes(val) ? 2 ** shortDirNames.indexOf(val) : 0)
            .reduce((acc, val) => acc + val, 0);
        if (this.customData.includes("C")) {
            this.tags |= getTagFlag("canopy");
            this.tags |= getTagFlag("blocks-tnt");
        }
    }
    shouldDie() {
        if (this.hasTag("canopy")) {
            // Remove all traces of the canopy
            this.tags &= ~(getTagFlag("canopy") | getTagFlag("blocks-tnt"));
            this.customData = this.customData.split("C").join("");
            return this.customData === "";
        }
        return true;
    }
    getLayer() {
        return Layer.SPECIAL;
    }
    blocks(_actor, otherMoveDirection) {
        return !!((2 ** ((otherMoveDirection + 2) % 4)) & this.allowedDirections);
    }
    exitBlocks(actor, otherMoveDirection) {
        if (actor.hasTag("ignores-exit-block"))
            return false;
        return !!((2 ** otherMoveDirection) & this.allowedDirections);
    }
}
ThinWall.tags = ["thinWall"];
ThinWall.extraTagProperties = ["extraTags"];
ThinWall.extraTags = ["blocks-tnt", "canopy"];
actorDB["thinWall"] = ThinWall;
export class InvisibleWall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "invisibleWall";
        this.animationLeft = 0;
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks() {
        return true;
    }
    bumped(other, direction) {
        if (this._internalCollisionIgnores(other, direction) ||
            other.hasTag("cc1block") ||
            other.hasTag("normal-monster"))
            return;
        this.animationLeft = 36;
    }
    onEachDecision() {
        if (this.animationLeft)
            this.animationLeft--;
    }
}
actorDB["invisibleWall"] = InvisibleWall;
export class AppearingWall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "appearingWall";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks() {
        return true;
    }
    bumped(other, direction) {
        var _a;
        if (this._internalCollisionIgnores(other, direction) ||
            other.hasTag("cc1block") ||
            other.hasTag("normal-monster"))
            return;
        if (other.hasTag("playable")) {
            (_a = this.level.sfxManager) === null || _a === void 0 ? void 0 : _a.playOnce("bump");
        }
        this.destroy(null, null);
        new Wall(this.level, this.tile.position);
    }
}
actorDB["appearingWall"] = AppearingWall;
export class BlueWall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "blueWall";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks(other) {
        return (this.customData === "real" ||
            other.hasTag("cc1block") ||
            other.hasTag("normal-monster"));
    }
    bumped(other, direction) {
        var _a;
        if (this._internalCollisionIgnores(other, direction) ||
            other.hasTag("cc1block") ||
            other.hasTag("normal-monster"))
            return;
        this.destroy(null, null);
        if (other.hasTag("playable")) {
            (_a = this.level.sfxManager) === null || _a === void 0 ? void 0 : _a.playOnce("bump");
        }
        if (this.customData === "real") {
            new Wall(this.level, this.tile.position);
        }
    }
}
BlueWall.tags = ["wall"];
actorDB["blueWall"] = BlueWall;
export class ToggleWall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "toggleWall";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks() {
        return this.customData === "on";
    }
    pulse() {
        this.customData = this.customData === "on" ? "off" : "on";
    }
    greenToggle() {
        this.customData = this.customData === "on" ? "off" : "on";
    }
}
onLevelAfterTick.push(level => {
    if (level.greenButtonPressed) {
        for (const terrain of level.actors) {
            if (hasOwnProperty(terrain, "greenToggle") &&
                typeof terrain.greenToggle === "function")
                terrain.greenToggle();
        }
        level.greenButtonPressed = false;
    }
});
actorDB["toggleWall"] = ToggleWall;
export class HoldWall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "holdWall";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks() {
        return this.customData === "on";
    }
    pulse() {
        this.customData = this.customData === "on" ? "off" : "on";
    }
    unpulse() {
        this.customData = this.customData === "on" ? "off" : "on";
    }
}
actorDB["holdWall"] = HoldWall;
/* export class SwivelRotatingPart extends Actor {
    id = "swivelRotatingPart"
    static immuneTags = ["tnt"]
    getLayer(): Layer {
        return Layer.SPECIAL
    }
    blocks(_actor: Actor, otherMoveDirection: Direction): boolean {
        return (
            otherMoveDirection === (this.direction + 2) % 4 ||
            otherMoveDirection === (this.direction + 3) % 4
        )
    }
    actorLeft(actor: Actor): void {
        if (actor.direction === this.direction) this.direction++
        else if (actor.direction === (this.direction + 1) % 4) this.direction += 3
        this.direction %= 4
    }
} */
export class Swivel extends Actor {
    constructor() {
        super(...arguments);
        this.id = "swivel";
    }
    // rotatingPart?: SwivelRotatingPart
    getLayer() {
        return Layer.STATIONARY;
    }
    /* levelStarted(): void {
        this.rotatingPart = new SwivelRotatingPart(this.level, this.tile.position)
        this.rotatingPart.direction = this.direction
    } */
    /* destroy(killer?: Actor | null, animType?: string | null): boolean {
        if (super.destroy(killer, animType)) {
            this.rotatingPart?.destroy(null, null)
            return true
        }
        return false
    }*/
    blocks(_actor, otherMoveDirection) {
        return (otherMoveDirection === (this.direction + 2) % 4 ||
            otherMoveDirection === (this.direction + 3) % 4);
    }
    actorLeft(actor) {
        var _a;
        const oldDir = this.direction;
        if (actor.direction === this.direction)
            this.direction++;
        else if (actor.direction === (this.direction + 1) % 4)
            this.direction += 3;
        this.direction %= 4;
        if (actor.hasTag("playable") && oldDir !== this.direction) {
            (_a = this.level.sfxManager) === null || _a === void 0 ? void 0 : _a.playOnce("door unlock");
        }
    }
    pulse() {
        this.direction += 1;
        this.direction %= 4;
    }
}
actorDB["swivel"] = Swivel;
export class GreenWall extends Actor {
    constructor() {
        super(...arguments);
        this.id = "greenWall";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
    blocks(other) {
        return this.customData === "real" || other.hasTag("block");
    }
}
GreenWall.tags = ["wall"];
actorDB["greenWall"] = GreenWall;
export class NoChipSign extends Actor {
    constructor() {
        super(...arguments);
        this.id = "noChipSign";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
}
NoChipSign.blockTags = ["chip"];
actorDB["noChipSign"] = NoChipSign;
export class NoMelindaSign extends Actor {
    constructor() {
        super(...arguments);
        this.id = "noMelindaSign";
    }
    getLayer() {
        return Layer.STATIONARY;
    }
}
NoMelindaSign.blockTags = ["melinda"];
actorDB["noMelindaSign"] = NoMelindaSign;
//# sourceMappingURL=walls.js.map